import {Component, Inject, OnInit} from '@angular/core';
import {MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef} from '@angular/material/legacy-dialog';
import {TranslateService} from '@ngx-translate/core';
import {DynamicFormConfig, DynamicFormReadyEvent, FilterSelectFieldConfig, SelectItem} from '@mydocma/dynamic-form';
import {EntityScopeEnum} from '@docma-mm-core/entity/entity-scope.enum';
import {Layout} from '@docma-mm-core/layout/layout';
import {MmProjectPermissionsService} from '../../../../mm-project/mm-project-permissions-service';
import {ProjectActionPermissionEnum} from '@docma-mm-core/permission/project-action-permission.enum';
import {ActionPermissionEnum} from '@docma-mm-core/permission/action-permission.enum';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {LayoutApiService} from '@docma-mm-core/layout/layout-api.service';
import { User } from '@docma-mm-core/user/user';
import { Project } from '@docma-mm-core/project/project';
import {MediaQueryService} from '@mydocma/core';
import {LayoutEditService} from '../layout-edit-service';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {getDuplicateName} from '@docma-mm-core/utils/utils';
import {DUPLICATE_MODE_FIELD_NAME, LayoutDuplicatDialogFormService, TYPE_FIELD_NAME} from './layout-duplicate-dialog-form.service';
import { Logger } from '@mydocma/common/src/lib/logger';

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-layout-duplicate-dialog',
  templateUrl: './layout-duplicate-dialog.component.html',
  styleUrls: ['./layout-duplicate-dialog.component.scss']
})
export class LayoutDuplicateDialogComponent implements OnInit {

  layoutDuplicateModes = LayoutDuplicateMode;
  mobileMode: boolean;

  duplicateMode: LayoutDuplicateMode;
  scopes: SelectItem[];
  selectedScope: SelectItem;
  isScopeForcedSubject = new BehaviorSubject<boolean>(false);
  isScopeForced$ = this.isScopeForcedSubject.asObservable();

  loadedLayout: Layout;
  loadingSubject = new BehaviorSubject<boolean>(true);
  loading$ = this.loadingSubject.asObservable();
  savingSubject = new BehaviorSubject<boolean>(false);
  saving$ = this.savingSubject.asObservable();

  titleSubject = new BehaviorSubject<string>(this.translateService.instant('MM.layout.layoutDuplicateMessage.title').replace(' - {{ value }}', ''));
  title$ = this.titleSubject.asObservable();

  currentNameSuffix: string;

  formConfig: DynamicFormConfig;
  formReadyEvent: DynamicFormReadyEvent;


  constructor(@Inject(MAT_DIALOG_DATA) public dialogData: {layoutId: number, project: Project, user: User, service: LayoutEditService, nameSuffix: string, layoutNames: { [scope: number]: string[] }},
              private dialogRef: MatDialogRef<LayoutDuplicateDialogComponent>,
              private readonly translateService: TranslateService,
              public readonly permissionService: MmProjectPermissionsService,
              private readonly layoutApiService: LayoutApiService,
              public readonly mediaQueryService: MediaQueryService,
              private readonly notificationService: NotificationService,
              private readonly dialogService: PromptDialogService,
              private readonly formService: LayoutDuplicatDialogFormService,
              private readonly logger: Logger) {

    this.initialize();
  }

  ngOnInit(): void {
  }

  initializeForm(event: DynamicFormReadyEvent) {
    this.formReadyEvent = event;
    const typeConfig = this.formReadyEvent.dynamicFormService.getFieldConfig(TYPE_FIELD_NAME);
    this.formReadyEvent.dynamicFormService.updateFieldConfig({
      ...typeConfig,
      source: this.scopes,
      value: this.selectedScope.id,
      label: { text: ' ' }
    } as FilterSelectFieldConfig);
  }

  onFormReady(event: DynamicFormReadyEvent) {
    this.initializeForm(event);
    this.formReadyEvent.dynamicFormEventsService.onFieldValueChanged
      .pipe(untilDestroyed(this))
      .subscribe((param: any) => {
        if (param.config.id === DUPLICATE_MODE_FIELD_NAME) {
          this.formReadyEvent.dynamicFormService.updateFieldConfig({
            ...this.formReadyEvent.dynamicFormService.getFieldConfig(TYPE_FIELD_NAME) as FilterSelectFieldConfig,
            readonly: parseInt(param.value, 10) as LayoutDuplicateMode === LayoutDuplicateMode.OnlyDuplicate
          });
        } else if (param.config.id === TYPE_FIELD_NAME) {
          const scopeId = param.value as number;
          const newScope = this.scopes.find(s => s.id === scopeId);
          if (newScope) {
            this.selectedScopeChanged(newScope);
          }
        }
      });
  }

  onResize(event) {
    this.mobileMode = event.contentRect.width <= 600;
  }

  async onSaveChanges() {
    this.savingSubject.next(true);

    try {
      this.loadedLayout.Name = this.loadedLayout.Name + this.currentNameSuffix;

      if (+this.loadedLayout.Scope !== this.selectedScope.id) {
        if (this.selectedScope.id === +EntityScopeEnum.Global) {
          this.loadedLayout.Project = null;
          this.loadedLayout.User = null;
          this.loadedLayout.Scope = EntityScopeEnum.Global;
        } else if (this.selectedScope.id === +EntityScopeEnum.Project) {
          this.loadedLayout.Project = new Project({Id: this.dialogData.project.Id});
          this.loadedLayout.User = null;
          this.loadedLayout.Scope = EntityScopeEnum.Project;
          this.loadedLayout.IsOverviewLayout = false;
        } else if (this.selectedScope.id === +EntityScopeEnum.User) {
          this.loadedLayout.Project = null;
          this.loadedLayout.User = new User({Id: this.dialogData.user.Id});
          this.loadedLayout.Scope = EntityScopeEnum.User;
          if (!!this.loadedLayout.IsFavoriteAll) {
            this.loadedLayout.IsFavoriteUser = this.loadedLayout.IsFavoriteAll;
          }
          this.loadedLayout.IsFavoriteAll = false;
        }
      }

      this.removeInvalidFilters();

      const savedLayout: Layout = await this.layoutApiService.createLayout(this.loadedLayout).toPromise();
      this.dialogRef.close({Layout: savedLayout});
    } catch (err) {
      this.logger.error(err, '', 'LayoutDuplicateDialogComponent');
      this.notificationService.error(this.translateService.instant('MM.layout.errorSavingLayout'));
    }
    finally {
      this.savingSubject.next(false);
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  initialize() {
    this.loadingSubject.next(true);

    this.currentNameSuffix = this.dialogData.nameSuffix;

    const hasEditProjectLayout = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditProjectLayout]);
    const hasEditGlobalLayout = this.permissionService.hasActionPermissions([ActionPermissionEnum.EditGlobalLayout]);

    const test = combineLatest([hasEditGlobalLayout, hasEditProjectLayout, this.layoutApiService.getFullLayout(this.dialogData.layoutId)])
      .pipe(untilDestroyed(this))
      .subscribe(([editGlobalPerm, editProjectPerm, loadedLayout]) => {
        this.loadedLayout = loadedLayout;

        this.titleSubject.next(this.translateService.instant('MM.layout.layoutDuplicateMessage.title', {value: this.loadedLayout.Name}));

        const source = Object.keys(EntityScopeEnum)
          .filter(key =>  !isNaN(Number(EntityScopeEnum[key]))
            && (EntityScopeEnum[key] === EntityScopeEnum.Global && editGlobalPerm
              || EntityScopeEnum[key] === EntityScopeEnum.Project && editProjectPerm
              || EntityScopeEnum[key] === EntityScopeEnum.User)
          )
          .map(key => ({
            id: EntityScopeEnum[key],
            text: this.translateService.instant('MM.layout.details.type' + key)
          }));

        this.scopes = source;

        this.selectedScope = this.scopes.find(x => x.id === +loadedLayout.Scope);

        if (!this.selectedScope) {
          this.isScopeForcedSubject.next(true);
          this.selectedScope = this.scopes.find(x => x.id === +EntityScopeEnum.User);
          this.duplicateMode = LayoutDuplicateMode.DuplicateAndChangeType;
        }
        else {
          this.duplicateMode = LayoutDuplicateMode.OnlyDuplicate;
        }

        loadedLayout.Id = undefined;
        loadedLayout.SUid = undefined;


        if (this.duplicateMode === LayoutDuplicateMode.OnlyDuplicate) {
          // console.log('LayoutDuplicateMode.OnlyDuplicate');
        }
        else if (this.duplicateMode === LayoutDuplicateMode.DuplicateAndChangeType) {
          // console.log('LayoutDuplicateMode.DuplicateAndChangeType');
          // const hasInvalidFilters = this.validateInvalidFilters(loadedLayout);
        }

        this.formConfig = this.formService.getFormConfig(
          this.duplicateMode,
          this.scopes,
          this.selectedScope.id,
          this.isScopeForcedSubject.value
        );

        this.loadingSubject.next(false);
      });
  }

  hasInvalidFilters(layout: Layout): boolean {
    let hasInvalidFilters: boolean;

    if (this.selectedScope.id === +EntityScopeEnum.Global) {
      hasInvalidFilters = layout.LayoutFilters?.some(x => x.Filter.Project != null || x.Filter.User != null);
    }
    else if (this.selectedScope.id === +EntityScopeEnum.Project) {
      hasInvalidFilters = layout.LayoutFilters?.some(x => x.Filter.User != null);
    }
    else if (this.selectedScope.id === +EntityScopeEnum.User) {
      hasInvalidFilters = layout.LayoutFilters?.some(x => x.Filter.Project != null);
    }
    return hasInvalidFilters;
  }

  removeInvalidFilters() {

    if (this.selectedScope.id === +EntityScopeEnum.Global) {
      for (const f of this.loadedLayout.LayoutFilters) {
        if (f.Filter.Project != null || f.Filter.User != null) {
          this.loadedLayout.LayoutFilters.splice(this.loadedLayout.LayoutFilters.findIndex(x => x.Filter.Id === f.Filter.Id), 1);
        }
      }
    }
    else if (this.selectedScope.id === +EntityScopeEnum.Project) {
      for (const f of this.loadedLayout.LayoutFilters) {
        if (f.Filter.User != null) {
          this.loadedLayout.LayoutFilters.splice(this.loadedLayout.LayoutFilters.findIndex(x => x.Filter.Id === f.Filter.Id), 1);
        }
      }
    }
    else if (this.selectedScope.id === +EntityScopeEnum.User) {
      for (const f of this.loadedLayout.LayoutFilters) {
        if (f.Filter.Project != null) {
          this.loadedLayout.LayoutFilters.splice(this.loadedLayout.LayoutFilters.findIndex(x => x.Filter.Id === f.Filter.Id), 1);
        }
      }
    }
  }

  selectedScopeChanged(newScope: SelectItem) {
    this.selectedScope = newScope;
    if (this.hasInvalidFilters(this.loadedLayout)) {
      this.dialogService.showNotificationDialog({ title: this.translateService.instant('MM.layout.messageChangeLayoutTypeTitle'), content: this.translateService.instant('MM.layout.messageChangeLayoutType')});
    }
    const scopeSwitched = (+this.loadedLayout.Scope !== this.selectedScope.id);
    this.currentNameSuffix = getDuplicateName(this.loadedLayout.Name, this.dialogData.layoutNames[this.selectedScope.id], this.translateService);
    // this.titleSubject.next(this.translateService.instant("MM.layout.layoutDuplicateMessage.title").replace('###layoutName###', `${this.loadedLayout.Name}${this.currentNameSuffix}`));
  }

  onDuplicateModeChanged(newMode: LayoutDuplicateMode) {
    this.duplicateMode = newMode;

    if (this.duplicateMode === LayoutDuplicateMode.OnlyDuplicate) {
      this.currentNameSuffix = getDuplicateName(this.loadedLayout.Name, this.dialogData.layoutNames[+EntityScopeEnum.User], this.translateService);
    }
  }
}

export enum LayoutDuplicateMode {
  OnlyDuplicate = 0,
  DuplicateAndChangeType = 1
}
